/**
 * Profile Model
 * Matches the Hub Profile data model structure
 * Reference: /american-drone-network-hub/src/data-models/profile.ts
 */

/**
 * Profile interface matching Hub Profile model
 * All fields use camelCase as per frontend conventions
 */
export interface Profile {
  // Base fields (from ModelBase)
  id: string;
  createdAt: Date | string;
  updatedAt: Date | string;
  deletedAt?: Date | string | null;
  createdBy?: string | null;
  updatedBy?: string | null;
  deletedBy?: string | null;

  // Stable identifiers
  profileUuid: string;  // Stable UUID that never changes
  firebaseUid: string;  // Firebase UID when linked

  // Basic profile information
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  avatar?: string;
  username?: string;
  language?: string;
  timezone?: string;

  // Two-Factor Authentication fields
  is2faEnabled?: boolean;
  twoFactorMethod?: 'sms' | 'totp' | null;
  twoFactorPhone?: string;
  twoFactorSecret?: string;
  backupCodesHash?: string;

  // Email verification
  emailVerified: boolean;
  emailVerificationSentAt?: Date | string | null;

  // Account lockout
  failedLoginAttempts?: number;
  lastFailedLoginAt?: Date | string | null;
  accountLockedUntil?: Date | string | null;
  requiresPasswordReset?: boolean;

  // Session tracking
  lastLogin?: Date | string | null;
  lastTokenRefresh?: Date | string | null;

  // Onboarding
  onboardingCompleted?: boolean;

  // Onboarding details
  dateOfBirth?: Date | string | null;
  entityType?: 'individual' | 'company' | 'farm' | null;
  entityName?: string | null;
  taxId?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  hasPart107?: boolean;
  part107Number?: string | null;
  part107Expiration?: Date | string | null;
  hasCommercialLicense?: boolean;
  licenseNumber?: string | null;
  membershipPlan?: 'pro' | 'core';
}

/**
 * Partial profile for updates
 */
export type ProfileUpdate = Partial<Profile>;

/**
 * Profile creation payload (fields required for registration)
 */
export interface ProfileCreate {
  firstName: string;
  lastName: string;
  email: string;
  firebaseUid: string;
  emailVerified?: boolean;
}
